import api from './api';

// ===== OPTIMIZED API CLIENT =====
// All pagination, filtering, sorting, and aggregation is done server-side
// The /fetch/all endpoint automatically returns aggregated platform data

export const takedownsApi = {
  // ===== TAKEDOWNS/CONTENT ENDPOINTS (Already Optimized) =====
  
  // Fetch optimized infringing content with pagination, filtering, and sorting
  fetchOptimizedContent: async (params = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.takedown_status) queryParams.append('takedown_status', params.takedown_status);
      if (params.violation_type) queryParams.append('violation_type', params.violation_type);
      if (params.media_type) queryParams.append('media_type', params.media_type);
      if (params.username) queryParams.append('username', params.username);
      if (params.search) queryParams.append('search', params.search);
      
      // Add sorting
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      const response = await api.get(`/infringingContent/fetch/allOptimized?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch counts and breakdowns (lightweight endpoint)
  fetchCounts: async (filters = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      
      // Add filters
      if (filters.takedown_status) queryParams.append('takedown_status', filters.takedown_status);
      if (filters.violation_type) queryParams.append('violation_type', filters.violation_type);
      if (filters.media_type) queryParams.append('media_type', filters.media_type);
      if (filters.username) queryParams.append('username', filters.username);
      if (filters.search) queryParams.append('search', filters.search);
      
      const response = await api.get(`/infringingContent/fetch/counts?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch all infringing content (complete dataset - use sparingly, for fallback only)
  fetchAllContent: async () => {
    try {
      const response = await api.get('/infringingContent/fetch/all');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== THREAT ACTORS ENDPOINTS (Newly Optimized) =====
  
  // Fetch all threat actors (fallback for edge cases)
  fetchAllThreats: async () => {
    try {
      const response = await api.get('/infringingContent/fetch/all/threats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch optimized threat actors with server-side pagination, filtering, and sorting
  fetchOptimizedThreats: async (params = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.search) queryParams.append('search', params.search);
      
      // Add sorting
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      const response = await api.get(`/infringingContent/fetch/all/threats?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch threat counts and breakdowns (server-side)
  fetchThreatCounts: async (filters = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      
      // Add filters
      if (filters.search) queryParams.append('search', filters.search);
      
      const response = await api.get(`/infringingContent/fetch/threatsCounts?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== PLATFORM DETAIL ENDPOINTS (Newly Optimized) =====
  
  // Fetch platform data by root domain (server-side filtering)
  // Uses /fetch/allOptimized which includes threatID for each content item
  fetchPlatformData: async (rootDomain, params = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      // Use search parameter to filter by root domain in post_url
      queryParams.append('search', rootDomain);
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.takedown_status) queryParams.append('takedown_status', params.takedown_status);
      if (params.violation_type) queryParams.append('violation_type', params.violation_type);
      if (params.media_type) queryParams.append('media_type', params.media_type);
      if (params.search) {
        // If there's a search term, combine it with root domain
        queryParams.set('search', `${rootDomain} ${params.search}`);
      }
      
      // Add sorting
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      const response = await api.get(`/infringingContent/fetch/allOptimized?${queryParams}`);
      
      // Add platform_stats if not present (calculate from data)
      if (!response.data.platform_stats && response.data.data) {
        const data = response.data.data;
        const total_sent = data.filter(item => 
          item.takedown_status === 'Submitted' || item.takedown_status === 'Removed'
        ).length;
        const complied = data.filter(item => item.takedown_status === 'Removed').length;
        const uncomplied = total_sent - complied;
        const compliance_rate = total_sent > 0 ? ((complied / total_sent) * 100).toFixed(2) : 0;
        
        response.data.platform_stats = {
          total_sent,
          complied,
          uncomplied,
          compliance_rate: parseFloat(compliance_rate)
        };
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch platform counts and breakdowns (server-side)
  fetchPlatformCounts: async (rootDomain, filters = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      // Use search parameter to filter by root domain
      queryParams.append('search', rootDomain);
      
      // Add filters
      if (filters.takedown_status) queryParams.append('takedown_status', filters.takedown_status);
      if (filters.violation_type) queryParams.append('violation_type', filters.violation_type);
      if (filters.media_type) queryParams.append('media_type', filters.media_type);
      if (filters.search) {
        // If there's a search term, combine it with root domain
        queryParams.set('search', `${rootDomain} ${filters.search}`);
      }
      
      const response = await api.get(`/infringingContent/fetch/counts?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== ALL PLATFORMS DASHBOARD ENDPOINTS (Newly Optimized) =====
  
  // Fetch all platforms with server-side aggregation and filtering
  // NOTE: /fetch/all already returns aggregated platform data when no specific filters are applied
  fetchAllPlatforms: async (params = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.compliance_rate) queryParams.append('compliance_rate', params.compliance_rate);
      if (params.violation_type) queryParams.append('violation_type', params.violation_type);
      if (params.search) queryParams.append('search', params.search);
      
      // Add sorting
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      // The /fetch/all endpoint already returns aggregated platform data
      const response = await api.get(`/infringingContent/fetch/all?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch platform counts for main dashboard
  // Uses pagination.total_items from /fetch/all endpoint
  fetchPlatformsCounts: async (filters = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      queryParams.append('page', 1);
      queryParams.append('limit', 1); // Only need pagination data, not actual records
      
      // Add filters to get filtered counts
      if (filters.compliance_rate) queryParams.append('compliance_rate', filters.compliance_rate);
      if (filters.violation_type) queryParams.append('violation_type', filters.violation_type);
      if (filters.search) queryParams.append('search', filters.search);
      
      const response = await api.get(`/infringingContent/fetch/all?${queryParams}`);
      
      return {
        total_items: response.data?.pagination?.total_items || 0,
        breakdown: {
          // Backend doesn't provide breakdown yet, but we can add it later
        },
      };
    } catch (error) {
      throw error;
    }
  },

  // ===== TAKEDOWN DETAILS ENDPOINTS =====
  
  // Fetch URLs for a specific content item
  fetchTakedownURLs: async (id) => {
    try {
      const response = await api.get(`/infringingContent/fetch/urls/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Fetch complete takedown details by combining multiple endpoints
  fetchTakedownDetails: async (id) => {
    try {
      
      // Fetch initial data to get post_url
      const urlsData = await api.get(`/infringingContent/fetch/urls/${id}`).catch(err => {
        return { data: { post_url: null, tos_url: null } };
      });
      
      // Fetch all other data in parallel (including searching by post_url for violation_type)
      const [imagesData, infoData, noticeData, contentData] = await Promise.all([
        api.get(`/images/fetch/${id}`).catch(err => {
          return { data: { image_urls: null, media_details: [] } };
        }),
        api.get(`/infringingContent/fetch/info/${id}`).catch(err => {
          return { data: {} };
        }),
        api.get(`/infringingContent/fetch/details/${id}`).catch(err => {
          return { data: {} };
        }),
        // Search by post_url to get violation_type
        urlsData.data?.post_url ? 
          api.get(`/infringingContent/fetch/allOptimized?search=${encodeURIComponent(urlsData.data.post_url)}&limit=1`).catch(err => {
            return { data: { data: [] } };
          }) : 
          Promise.resolve({ data: { data: [] } }),
      ]);
      
      // Extract the content item from search results
      const contentItem = contentData.data?.data?.[0] || {};
      
      // Combine all data into a single object
      const combinedData = {
        id,
        // From imagesData
        image_urls: imagesData.data?.image_urls || null,
        media_details: imagesData.data?.media_details || [],
        // From urlsData
        post_url: urlsData.data?.post_url || null,
        tos_url: urlsData.data?.tos_url || null,
        // From infoData
        takedown_status: infoData.data?.takedown_status || contentItem.takedown_status || 'Unknown',
        channel_type: infoData.data?.channel_type || contentItem.channel_type || 'Unknown',
        host_name: infoData.data?.host_name || contentItem.host_name || 'Unknown',
        host_country: infoData.data?.host_country || contentItem.host_country || 'Unknown',
        username: infoData.data?.username || contentItem.username || 'Unknown',
        views: infoData.data?.views || contentItem.views || 0,
        likes: infoData.data?.likes || contentItem.likes || 0,
        comments: infoData.data?.comments || contentItem.comments || 0,
        threatId: infoData.data?.threatId || contentItem.threatID || null,
        media_type: infoData.data?.media_type || contentItem.media_type || 'Unknown',
        last_activity: infoData.data?.last_activity || contentItem.last_activity || null,
        detected_date: infoData.data?.detected_date || contentItem.detected_date || null,
        // Get violation_type from contentData (allOptimized endpoint)
        violation_type: contentItem.violation_type || 'Unknown',
        // From noticeData
        takedownID: noticeData.data?.takedownID || id,
        notice_type: noticeData.data?.notice_type || null,
        notice_requirements: noticeData.data?.notice_requirements || null,
        takedown_notice_content: noticeData.data?.takedown_notice_content || null,
        notice_contact_info: noticeData.data?.notice_contact_info || null,
        notice_last_edited: noticeData.data?.notice_last_edited || null,
        notice_last_edit_by: noticeData.data?.notice_last_edit_by || null,
        notice_status: noticeData.data?.notice_status || 'Unknown',
      };
      
      return combinedData;
    } catch (error) {
      throw error;
    }
  },
  
  // Update notice status for a specific content ID
  updateNoticeStatus: async (id, noticeStatus) => {
    try {
      const response = await api.post(`/infringingContent/set/details/${id}`, {
        notice_status: noticeStatus,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== THREAT ACTOR DETAILS ENDPOINTS =====
  
  // Fetch comprehensive threat actor details
  fetchThreatDetails: async (id) => {
    try {
      const response = await api.get(`/infringingContent/fetch/threats/details/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch infringement types for a specific threat actor
  fetchThreatInfringementTypes: async (id) => {
    try {
      const response = await api.get(`/infringingContent/fetch/threats/infringement-types/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get distinct infringement types for a threat actor
  getDistinctInfringementTypes: async (id) => {
    try {
      const response = await api.get(`/infringingContent/fetch/threats/infringement-types/${id}`);
      return response.data.infringement_types?.map(type => type.violation_type) || [];
    } catch (error) {
      return [];
    }
  },

  // ===== CONTENT REVIEW ENDPOINTS =====
  
  // Get list of content IDs to review (takedown_status = "Pending")
  getContentToReview: async (limit = 20, offset = 0) => {
    try {
      const response = await api.get(`/contentReview/getContentToReview?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get detailed review information for a specific content item
  getContentDetails: async (id) => {
    try {
      const response = await api.get(`/contentReview/getContentDetails/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Accept content for takedown (Approved status)
  acceptContent: async (id) => {
    try {
      const response = await api.post(`/contentReview/setAccept/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Put content on hold (No Action status)
  holdContent: async (id) => {
    try {
      const response = await api.post(`/contentReview/setHold/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reject content
  rejectContent: async (id) => {
    try {
      const response = await api.post(`/contentReview/setReject/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== OVERVIEW DASHBOARD ENDPOINTS =====
  
  // Fetch overview statistics
  fetchOverviewStats: async (dateRange = 'all_time') => {
    try {
      const response = await api.get(`/overviewDash/stats?date_range=${dateRange}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch total count of pending takedowns (all-time, no date filtering)
  fetchOverviewPending: async () => {
    try {
      const response = await api.get(`/overviewDash/pending`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch infringement activity breakdown for takedowns
  fetchInfringementActivityTakedowns: async (dateRange = 'all_time', status = 'Submitted') => {
    try {
      const response = await api.get(`/overviewDash/infringement-activity/takedowns?date_range=${dateRange}&status=${status}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch infringement activity breakdown by infringement types
  fetchInfringementActivityTypes: async (dateRange = 'all_time') => {
    try {
      const response = await api.get(`/overviewDash/infringement-activity/infringement-types?date_range=${dateRange}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch infringement activity breakdown by platforms
  fetchInfringementActivityPlatforms: async (dateRange = 'all_time') => {
    try {
      const response = await api.get(`/overviewDash/infringement-activity/platforms?date_range=${dateRange}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch infringement activity breakdown by threat actors
  fetchInfringementActivityThreatActors: async (dateRange = 'all_time') => {
    try {
      const response = await api.get(`/overviewDash/infringement-activity/threat-actors?date_range=${dateRange}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Fetch top platforms by takedown count (deprecated - use fetchInfringementActivityTakedowns instead)
  fetchTopPlatforms: async (dateRange = 'all_time') => {
    try {
      const response = await api.get(`/overviewDash/takedowns?date_range=${dateRange}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Submit new infringing content
  submitNewContent: async (url, infringementType, additionalNotes = '') => {
    try {
      const response = await api.post('/infringingContent/submitnew', {
        url,
        infringementType,
        additionalNotes
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default takedownsApi;
